import { useState, useEffect } from 'react'
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  X,
  Package,
  Save,
  Filter,
  ChevronDown,
  RefreshCw,
  AlertCircle,
  Tag,
  Layers
} from 'lucide-react'
import { api, Product as ApiProduct, Department, Brand } from '@/lib/api'
import ComboBox from '@/components/ui/ComboBox'

// Mock products data (fallback)
const mockProducts = [
  { id: '1', name: 'India Gate Basmati Rice', brand: 'India Gate', brandId: '', department: 'Rice & Grains', departmentId: '', price: 599, mrp: 699, stock: 150, sku: 'IG-RICE-001', size: '5kg' },
  { id: '2', name: 'Everest Garam Masala', brand: 'Everest', brandId: '', department: 'Spices & Masalas', departmentId: '', price: 85, mrp: 95, stock: 300, sku: 'EVR-GM-001', size: '100g' },
  { id: '3', name: 'Tata Premium Tea', brand: 'Tata', brandId: '', department: 'Beverages', departmentId: '', price: 380, mrp: 420, stock: 160, sku: 'TATA-TEA-001', size: '1kg' },
  { id: '4', name: 'Haldiram Aloo Bhujia', brand: 'Haldiram', brandId: '', department: 'Snacks & Namkeen', departmentId: '', price: 110, mrp: 125, stock: 175, sku: 'HAL-AB-001', size: '400g' },
  { id: '5', name: 'Aashirvaad Whole Wheat Atta', brand: 'Aashirvaad', brandId: '', department: 'Rice & Grains', departmentId: '', price: 450, mrp: 500, stock: 200, sku: 'ASH-ATTA-001', size: '10kg' },
  { id: '6', name: 'MDH Chana Masala', brand: 'MDH', brandId: '', department: 'Spices & Masalas', departmentId: '', price: 75, mrp: 85, stock: 250, sku: 'MDH-CM-001', size: '100g' },
  { id: '7', name: 'Tata Sampann Toor Dal', brand: 'Tata', brandId: '', department: 'Dals & Pulses', departmentId: '', price: 145, mrp: 160, stock: 220, sku: 'TATA-TD-001', size: '1kg' },
  { id: '8', name: 'Everest Turmeric Powder', brand: 'Everest', brandId: '', department: 'Spices & Masalas', departmentId: '', price: 120, mrp: 140, stock: 180, sku: 'EVR-TUR-001', size: '200g' },
]

const defaultDepartments = ['All Departments', 'Rice & Grains', 'Spices & Masalas', 'Dals & Pulses', 'Snacks & Namkeen', 'Beverages']
const defaultBrands = ['All Brands', 'India Gate', 'Everest', 'Tata', 'Haldiram', 'Aashirvaad', 'MDH']

interface Product {
  id: string
  name: string
  brand: string
  brandId: string
  department: string
  departmentId: string
  price: number
  mrp: number
  stock: number
  sku: string
  size: string
}

export default function ProductsPage() {
  const [products, setProducts] = useState<Product[]>(mockProducts)
  const [departments, setDepartments] = useState<string[]>(defaultDepartments)
  const [brands, setBrands] = useState<string[]>(defaultBrands)
  const [departmentMap, setDepartmentMap] = useState<Record<string, string>>({})
  const [brandMap, setBrandMap] = useState<Record<string, string>>({})
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedDepartment, setSelectedDepartment] = useState('All Departments')
  const [selectedBrand, setSelectedBrand] = useState('All Brands')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isApiConnected, setIsApiConnected] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isNewBrand, setIsNewBrand] = useState(false)
  const [isNewDepartment, setIsNewDepartment] = useState(false)
  const [isEditNewBrand, setIsEditNewBrand] = useState(false)
  const [isEditNewDepartment, setIsEditNewDepartment] = useState(false)
  const [newProduct, setNewProduct] = useState({
    name: '',
    brand: '',
    brandId: '',
    department: '',
    departmentId: '',
    price: '',
    mrp: '',
    stock: '',
    sku: '',
    size: ''
  })

  // Load data from API or use mock data
  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setIsLoading(true)
    try {
      // Try to load from API
      const [productsRes, deptRes, brandRes] = await Promise.all([
        api.products.getAll({ perPage: 100 }),
        api.admin.departments.getAll(),
        api.admin.brands.getAll(),
      ])

      if (productsRes.success && productsRes.data) {
        const mappedProducts = productsRes.data.map((p: ApiProduct) => ({
          id: p.product_id,
          name: p.product_name,
          brand: p.brand?.brand_name || 'Unknown',
          brandId: p.brand_id || '',
          department: p.department?.department_name || 'Unknown',
          departmentId: p.department_id || '',
          price: Number(p.price),
          mrp: Number(p.mrp) || Number(p.price),
          stock: p.stock_quantity,
          sku: p.sku,
          size: p.size || '',
        }))
        setProducts(mappedProducts)
        setIsApiConnected(true)
      }

      if (deptRes.success && deptRes.data) {
        const deptNames = ['All Departments', ...deptRes.data.map((d: Department) => d.department_name)]
        setDepartments(deptNames)
        const dMap: Record<string, string> = {}
        deptRes.data.forEach((d: Department) => {
          dMap[d.department_name] = d.department_id
        })
        setDepartmentMap(dMap)
      }

      if (brandRes.success && brandRes.data) {
        const brandNames = ['All Brands', ...brandRes.data.map((b: Brand) => b.brand_name)]
        setBrands(brandNames)
        const bMap: Record<string, string> = {}
        brandRes.data.forEach((b: Brand) => {
          bMap[b.brand_name] = b.brand_id
        })
        setBrandMap(bMap)
      }
    } catch (error) {
      console.log('API not available, using mock data')
      setIsApiConnected(false)
    }
    setIsLoading(false)
  }

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.brand.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesDepartment = selectedDepartment === 'All Departments' || product.department === selectedDepartment
    const matchesBrand = selectedBrand === 'All Brands' || product.brand === selectedBrand
    return matchesSearch && matchesDepartment && matchesBrand
  })

  // Add new product
  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSaving(true)

    let brandId = departmentMap[newProduct.brand]
    let departmentId = departmentMap[newProduct.department]

    try {
      if (isApiConnected) {
        // Create new brand if needed
        if (isNewBrand && newProduct.brand.trim()) {
          try {
            const brandResponse = await api.admin.brands.create({
              brand_name: newProduct.brand.trim(),
              slug: newProduct.brand.toLowerCase().replace(/\s+/g, '-'),
              is_active: true,
            } as any)
            if (brandResponse.success) {
              brandId = brandResponse.data.brand_id
              // Update local brand list
              setBrands(prev => [...prev, newProduct.brand.trim()])
              setBrandMap(prev => ({ ...prev, [newProduct.brand.trim()]: brandId }))
            }
          } catch (err) {
            console.log('Failed to create brand, continuing without it')
          }
        }

        // Create new department if needed
        if (isNewDepartment && newProduct.department.trim()) {
          try {
            const deptResponse = await api.admin.departments.create({
              department_name: newProduct.department.trim(),
              slug: newProduct.department.toLowerCase().replace(/\s+/g, '-'),
              is_active: true,
            } as any)
            if (deptResponse.success) {
              departmentId = deptResponse.data.department_id
              // Update local department list
              setDepartments(prev => [...prev, newProduct.department.trim()])
              setDepartmentMap(prev => ({ ...prev, [newProduct.department.trim()]: departmentId }))
            }
          } catch (err) {
            console.log('Failed to create department, continuing without it')
          }
        }

        // Create the product
        const productData = {
          product_name: newProduct.name,
          sku: newProduct.sku,
          price: parseFloat(newProduct.price),
          mrp: parseFloat(newProduct.mrp),
          stock_quantity: parseInt(newProduct.stock),
          size: newProduct.size,
          department_id: departmentId || departmentMap[newProduct.department] || undefined,
          brand_id: brandId || brandMap[newProduct.brand] || undefined,
        }

        const response = await api.admin.products.create(productData)
        if (response.success) {
          await loadData() // Reload all data
          resetAddForm()
          return
        }
      }
    } catch (error) {
      console.log('API error, falling back to local state')
    }

    // Fallback: Add to local state (also update brands/departments lists locally)
    if (isNewBrand && newProduct.brand.trim()) {
      setBrands(prev => [...prev, newProduct.brand.trim()])
    }
    if (isNewDepartment && newProduct.department.trim()) {
      setDepartments(prev => [...prev, newProduct.department.trim()])
    }

    const product: Product = {
      id: String(Date.now()),
      name: newProduct.name,
      brand: newProduct.brand,
      brandId: '',
      department: newProduct.department,
      departmentId: '',
      price: parseFloat(newProduct.price),
      mrp: parseFloat(newProduct.mrp),
      stock: parseInt(newProduct.stock),
      sku: newProduct.sku,
      size: newProduct.size
    }
    setProducts([product, ...products])
    resetAddForm()
  }

  const resetAddForm = () => {
    setShowAddModal(false)
    setNewProduct({ name: '', brand: '', brandId: '', department: '', departmentId: '', price: '', mrp: '', stock: '', sku: '', size: '' })
    setIsNewBrand(false)
    setIsNewDepartment(false)
    setIsSaving(false)
  }

  // Edit product
  const handleEditProduct = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingProduct) return
    setIsSaving(true)

    let brandId = brandMap[editingProduct.brand]
    let departmentId = departmentMap[editingProduct.department]

    try {
      if (isApiConnected) {
        // Create new brand if needed
        if (isEditNewBrand && editingProduct.brand.trim()) {
          try {
            const brandResponse = await api.admin.brands.create({
              brand_name: editingProduct.brand.trim(),
              slug: editingProduct.brand.toLowerCase().replace(/\s+/g, '-'),
              is_active: true,
            } as any)
            if (brandResponse.success) {
              brandId = brandResponse.data.brand_id
              setBrands(prev => [...prev, editingProduct.brand.trim()])
              setBrandMap(prev => ({ ...prev, [editingProduct.brand.trim()]: brandId }))
            }
          } catch (err) {
            console.log('Failed to create brand')
          }
        }

        // Create new department if needed
        if (isEditNewDepartment && editingProduct.department.trim()) {
          try {
            const deptResponse = await api.admin.departments.create({
              department_name: editingProduct.department.trim(),
              slug: editingProduct.department.toLowerCase().replace(/\s+/g, '-'),
              is_active: true,
            } as any)
            if (deptResponse.success) {
              departmentId = deptResponse.data.department_id
              setDepartments(prev => [...prev, editingProduct.department.trim()])
              setDepartmentMap(prev => ({ ...prev, [editingProduct.department.trim()]: departmentId }))
            }
          } catch (err) {
            console.log('Failed to create department')
          }
        }

        const response = await api.admin.products.update(editingProduct.id, {
          product_name: editingProduct.name,
          sku: editingProduct.sku,
          price: editingProduct.price,
          mrp: editingProduct.mrp,
          stock_quantity: editingProduct.stock,
          size: editingProduct.size,
          department_id: departmentId || departmentMap[editingProduct.department] || undefined,
          brand_id: brandId || brandMap[editingProduct.brand] || undefined,
        })
        if (response.success) {
          await loadData()
          resetEditForm()
          return
        }
      }
    } catch (error) {
      console.log('API error, falling back to local state')
    }

    // Fallback: Update local state
    if (isEditNewBrand && editingProduct.brand.trim()) {
      setBrands(prev => [...prev, editingProduct.brand.trim()])
    }
    if (isEditNewDepartment && editingProduct.department.trim()) {
      setDepartments(prev => [...prev, editingProduct.department.trim()])
    }

    setProducts(products.map(p => p.id === editingProduct.id ? editingProduct : p))
    resetEditForm()
  }

  const resetEditForm = () => {
    setShowEditModal(false)
    setEditingProduct(null)
    setIsEditNewBrand(false)
    setIsEditNewDepartment(false)
    setIsSaving(false)
  }

  // Delete product
  const handleDeleteProduct = async (id: string) => {
    if (!confirm('Are you sure you want to delete this product?')) return

    try {
      if (isApiConnected) {
        const response = await api.admin.products.delete(id)
        if (response.success) {
          await loadData()
          return
        }
      }
    } catch (error) {
      console.log('API error, falling back to local state')
    }

    // Fallback: Remove from local state
    setProducts(products.filter(p => p.id !== id))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-500">Loading products...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Products</h1>
          <p className="text-gray-500 mt-1">Manage your product catalog</p>
        </div>
        <div className="flex items-center gap-3">
          {!isApiConnected && (
            <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-2 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4" />
              Demo Mode
            </div>
          )}
          <button
            onClick={loadData}
            className="btn-secondary flex items-center gap-2"
            title="Refresh data"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add New Product
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search products by name, SKU, or brand..."
              className="input-field pl-12"
            />
          </div>

          {/* Department Filter */}
          <div className="relative">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="input-field pl-12 pr-10 appearance-none cursor-pointer min-w-[200px]"
            >
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>

          {/* Brand Filter */}
          <div className="relative">
            <Package className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="input-field pl-12 pr-10 appearance-none cursor-pointer min-w-[180px]"
            >
              {brands.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold">Product</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Brand</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Department</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Cost</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Selling Price</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Stock</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No products found</p>
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => (
                  <tr key={product.id} className="table-row">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-800">{product.name}</p>
                        <p className="text-sm text-gray-500">{product.sku} • {product.size}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{product.brand}</td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                        {product.department}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-600">${product.price}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-semibold text-green-600">${product.mrp}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        product.stock > 100 ? 'bg-green-100 text-green-700' :
                        product.stock > 50 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {product.stock} units
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditingProduct(product)
                            setShowEditModal(true)
                          }}
                          className="p-2 hover:bg-blue-50 rounded-lg text-blue-600 transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product.id)}
                          className="p-2 hover:bg-red-50 rounded-lg text-red-600 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Results count */}
        <div className="px-6 py-4 border-t border-primary-100 bg-primary-50">
          <p className="text-sm text-gray-600">
            Showing <strong>{filteredProducts.length}</strong> of <strong>{products.length}</strong> products
            {isApiConnected && <span className="text-green-600 ml-2">• Connected to API</span>}
          </p>
        </div>
      </div>

      {/* Add Product Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fade-in">
            <div className="p-6 border-b border-primary-100 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-800">Add New Product</h2>
              <button
                onClick={resetAddForm}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddProduct} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
                  <input
                    type="text"
                    required
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                    className="input-field"
                    placeholder="e.g., India Gate Basmati Rice"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">SKU *</label>
                  <input
                    type="text"
                    required
                    value={newProduct.sku}
                    onChange={(e) => setNewProduct({ ...newProduct, sku: e.target.value })}
                    className="input-field"
                    placeholder="e.g., IG-RICE-001"
                  />
                </div>
                <ComboBox
                  label="Brand"
                  value={newProduct.brand}
                  onChange={(value, isNew) => {
                    setNewProduct({ ...newProduct, brand: value })
                    setIsNewBrand(isNew)
                  }}
                  options={brands.slice(1)}
                  placeholder="Select or type brand name..."
                  required
                  allowNew
                  newItemLabel="Create brand"
                  icon={<Tag className="w-5 h-5" />}
                />
                <ComboBox
                  label="Department"
                  value={newProduct.department}
                  onChange={(value, isNew) => {
                    setNewProduct({ ...newProduct, department: value })
                    setIsNewDepartment(isNew)
                  }}
                  options={departments.slice(1)}
                  placeholder="Select or type department name..."
                  required
                  allowNew
                  newItemLabel="Create department"
                  icon={<Layers className="w-5 h-5" />}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cost Price ($) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={newProduct.price}
                    onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                    className="input-field"
                    placeholder="10.99"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Selling Price ($) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={newProduct.mrp}
                    onChange={(e) => setNewProduct({ ...newProduct, mrp: e.target.value })}
                    className="input-field"
                    placeholder="14.99"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stock *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={newProduct.stock}
                    onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                    className="input-field"
                    placeholder="100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Size *</label>
                  <input
                    type="text"
                    required
                    value={newProduct.size}
                    onChange={(e) => setNewProduct({ ...newProduct, size: e.target.value })}
                    className="input-field"
                    placeholder="e.g., 5kg, 100g, 1L"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-4 pt-4">
                <button
                  type="button"
                  onClick={resetAddForm}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" disabled={isSaving} className="btn-primary flex items-center gap-2">
                  {isSaving ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <Save className="w-5 h-5" />
                  )}
                  {isSaving ? 'Saving...' : 'Add Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {showEditModal && editingProduct && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fade-in">
            <div className="p-6 border-b border-primary-100 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-800">Edit Product</h2>
              <button
                onClick={resetEditForm}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleEditProduct} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
                  <input
                    type="text"
                    required
                    value={editingProduct.name}
                    onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">SKU *</label>
                  <input
                    type="text"
                    required
                    value={editingProduct.sku}
                    onChange={(e) => setEditingProduct({ ...editingProduct, sku: e.target.value })}
                    className="input-field"
                  />
                </div>
                <ComboBox
                  label="Brand"
                  value={editingProduct.brand}
                  onChange={(value, isNew) => {
                    setEditingProduct({ ...editingProduct, brand: value })
                    setIsEditNewBrand(isNew)
                  }}
                  options={brands.slice(1)}
                  placeholder="Select or type brand name..."
                  required
                  allowNew
                  newItemLabel="Create brand"
                  icon={<Tag className="w-5 h-5" />}
                />
                <ComboBox
                  label="Department"
                  value={editingProduct.department}
                  onChange={(value, isNew) => {
                    setEditingProduct({ ...editingProduct, department: value })
                    setIsEditNewDepartment(isNew)
                  }}
                  options={departments.slice(1)}
                  placeholder="Select or type department name..."
                  required
                  allowNew
                  newItemLabel="Create department"
                  icon={<Layers className="w-5 h-5" />}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cost Price ($) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={editingProduct.price}
                    onChange={(e) => setEditingProduct({ ...editingProduct, price: parseFloat(e.target.value) })}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Selling Price ($) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={editingProduct.mrp}
                    onChange={(e) => setEditingProduct({ ...editingProduct, mrp: parseFloat(e.target.value) })}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stock *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={editingProduct.stock}
                    onChange={(e) => setEditingProduct({ ...editingProduct, stock: parseInt(e.target.value) })}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Size *</label>
                  <input
                    type="text"
                    required
                    value={editingProduct.size}
                    onChange={(e) => setEditingProduct({ ...editingProduct, size: e.target.value })}
                    className="input-field"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-4 pt-4">
                <button
                  type="button"
                  onClick={resetEditForm}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" disabled={isSaving} className="btn-primary flex items-center gap-2">
                  {isSaving ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <Save className="w-5 h-5" />
                  )}
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
